import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/MyTasks.css';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../utils/api';
import EvaluationResults from './EvaluationResults';

function MyTasks() {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);

  const fetchMyTasks = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const url = `${API_BASE_URL}/tasks/my-tasks${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithAuth(url, {}, session);

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      setTasks(data.data || []);
      setStats(data.stats || null);
      setTotalEarnings(data.totalEarnings || 0);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load your tasks. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user, session, filterStatus]);

  useEffect(() => {
    fetchMyTasks();
  }, [fetchMyTasks]);

  const handleStartTask = async (taskId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tasks/${taskId}/start`, {
        method: 'POST'
      }, session);

      if (!response.ok) {
        throw new Error('Failed to start task');
      }

      fetchMyTasks();
    } catch (err) {
      console.error('Error starting task:', err);
      alert('Failed to start task. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'claimed':
        return '#f59e0b';
      case 'in_progress':
        return '#8b5cf6';
      case 'submitted':
        return '#06b6d4';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'claimed':
        return '📋';
      case 'in_progress':
        return '🔨';
      case 'submitted':
        return '📤';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      default:
        return '📄';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="my-tasks">
        <div className="login-prompt">
          <h2>Please log in to view your tasks</h2>
          <p>You need to be authenticated to see your claimed and completed tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-tasks">
      <div className="my-tasks-header">
        <div className="header-content">
          <h1>My Tasks</h1>
          <p className="header-subtitle">Track your claimed tasks and earnings</p>
        </div>
        <button className="browse-tasks-btn" onClick={() => navigate('/tasks')}>
          Browse Available Tasks
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="stats-overview">
          <div className="stat-card earnings">
            <div className="stat-value">{formatCurrency(totalEarnings)}</div>
            <div className="stat-label">Total Earned</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Tasks</div>
          </div>
          <div className="stat-card" style={{ '--stat-accent': '#f59e0b' }}>
            <div className="stat-value">{stats.claimed}</div>
            <div className="stat-label">Claimed</div>
          </div>
          <div className="stat-card" style={{ '--stat-accent': '#8b5cf6' }}>
            <div className="stat-value">{stats.in_progress}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card" style={{ '--stat-accent': '#06b6d4' }}>
            <div className="stat-value">{stats.submitted}</div>
            <div className="stat-label">Submitted</div>
          </div>
          <div className="stat-card" style={{ '--stat-accent': '#10b981' }}>
            <div className="stat-value">{stats.approved}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card" style={{ '--stat-accent': '#ef4444' }}>
            <div className="stat-value">{stats.rejected}</div>
            <div className="stat-label">Rejected</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Tasks</option>
            <option value="claimed">Claimed</option>
            <option value="in_progress">In Progress</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="loading-message">Loading your tasks...</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!loading && !error && tasks.length === 0 && (
        <div className="empty-state">
          <h3>No tasks yet</h3>
          <p>You haven't claimed any tasks. Browse the marketplace to find work!</p>
          <button className="browse-tasks-btn" onClick={() => navigate('/tasks')}>
            Browse Tasks
          </button>
        </div>
      )}

      {/* Task List */}
      <div className="tasks-list">
        {tasks.map((task) => (
          <div key={task.id} className="task-card" onClick={() => setSelectedTask(task)}>
            <div className="task-card-header">
              <div className="task-status-indicator" style={{ backgroundColor: getStatusColor(task.status) }}>
                <span className="status-icon">{getStatusIcon(task.status)}</span>
                <span className="status-text">{task.status.replace('_', ' ')}</span>
              </div>
              <div className="task-bounty">{formatCurrency(task.bounty_portion)}</div>
            </div>

            <h3 className="task-title">{task.title}</h3>

            <p className="task-description">
              {task.description?.length > 150
                ? `${task.description.substring(0, 150)}...`
                : task.description}
            </p>

            <div className="task-meta">
              <div className="meta-item">
                <span className="meta-label">Project:</span>
                <span className="meta-value">{task.problem?.title || 'Unknown'}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Complexity:</span>
                <span className="meta-value">{task.complexity}</span>
              </div>
            </div>

            <div className="task-timeline">
              {task.claimed_at && (
                <div className="timeline-item">
                  <span className="timeline-label">Claimed:</span>
                  <span className="timeline-value">{formatDate(task.claimed_at)}</span>
                </div>
              )}
              {task.submitted_at && (
                <div className="timeline-item">
                  <span className="timeline-label">Submitted:</span>
                  <span className="timeline-value">{formatDate(task.submitted_at)}</span>
                </div>
              )}
              {task.reviewed_at && (
                <div className="timeline-item">
                  <span className="timeline-label">Reviewed:</span>
                  <span className="timeline-value">{formatDate(task.reviewed_at)}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="task-actions">
              {task.status === 'claimed' && (
                <button
                  className="action-btn start-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartTask(task.id);
                  }}
                >
                  Start Working
                </button>
              )}
              {['claimed', 'in_progress', 'rejected'].includes(task.status) && (
                <button
                  className="action-btn submit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTask(task);
                  }}
                >
                  {task.status === 'rejected' ? 'Resubmit' : 'Submit Solution'}
                </button>
              )}
              {task.status === 'approved' && (
                <span className="earned-badge">Earned {formatCurrency(task.bounty_portion)}</span>
              )}
            </div>

            {/* Show evaluation score if available */}
            {task.evaluation && task.evaluation.length > 0 && (
              <div className="task-evaluation-preview">
                <span className="eval-label">Quality Score:</span>
                <span
                  className="eval-score"
                  style={{
                    color: task.evaluation[0].quality_score >= 70 ? '#10b981' : task.evaluation[0].quality_score >= 50 ? '#f59e0b' : '#ef4444'
                  }}
                >
                  {task.evaluation[0].quality_score}/100
                </span>
              </div>
            )}

            {/* Show error status indicators */}
            {task.evaluation_status === 'failed' && (
              <div className="task-error-preview">
                <span className="error-icon">⚠️</span>
                <span className="error-text">Evaluation failed - click for details</span>
              </div>
            )}
            {task.rating_update_status === 'failed' && (
              <div className="task-error-preview">
                <span className="error-icon">⚠️</span>
                <span className="error-text">Rating update failed - click for details</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="task-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-modal-header">
              <h2>Task Details</h2>
              <button className="close-modal-btn" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>

            <div className="task-modal-content">
              <div className="task-detail-header">
                <h3>{selectedTask.title}</h3>
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(selectedTask.status) }}
                >
                  {selectedTask.status.replace('_', ' ')}
                </span>
              </div>

              <div className="task-detail-section">
                <h4>Description</h4>
                <p>{selectedTask.description}</p>
              </div>

              <div className="task-detail-section">
                <h4>Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Project</span>
                    <span className="detail-value">{selectedTask.problem?.title}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Bounty</span>
                    <span className="detail-value">{formatCurrency(selectedTask.bounty_portion)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Priority</span>
                    <span className="detail-value">{selectedTask.priority}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Complexity</span>
                    <span className="detail-value">{selectedTask.complexity}</span>
                  </div>
                  {selectedTask.estimated_hours && (
                    <div className="detail-item">
                      <span className="detail-label">Est. Hours</span>
                      <span className="detail-value">{selectedTask.estimated_hours}h</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTask.acceptance_criteria && selectedTask.acceptance_criteria.length > 0 && (
                <div className="task-detail-section">
                  <h4>Acceptance Criteria</h4>
                  <ul className="criteria-list">
                    {selectedTask.acceptance_criteria.map((criteria, idx) => (
                      <li key={idx}>{criteria}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedTask.solution_text && (
                <div className="task-detail-section">
                  <h4>Your Solution</h4>
                  <div className="solution-content">
                    <pre>{selectedTask.solution_text}</pre>
                  </div>
                  {selectedTask.github_pr_url && (
                    <div className="github-link">
                      <strong>GitHub PR:</strong>{' '}
                      <a href={selectedTask.github_pr_url} target="_blank" rel="noopener noreferrer">
                        {selectedTask.github_pr_url}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Evaluation Results */}
              {selectedTask.evaluation && selectedTask.evaluation.length > 0 && (
                <div className="task-detail-section">
                  <h4>Evaluation Results</h4>
                  <EvaluationResults evaluation={selectedTask.evaluation[0]} />
                </div>
              )}

              {/* Rejection feedback */}
              {selectedTask.status === 'rejected' && selectedTask.rejection_feedback && (
                <div className="task-detail-section rejection-feedback">
                  <h4>Rejection Feedback</h4>
                  <p>{selectedTask.rejection_feedback}</p>
                </div>
              )}

              {/* Evaluation error (if evaluation failed) */}
              {selectedTask.evaluation_status === 'failed' && selectedTask.evaluation_error && (
                <div className="task-detail-section evaluation-error">
                  <h4>Evaluation Error</h4>
                  <p>{selectedTask.evaluation_error}</p>
                  <p className="error-hint">The automated evaluation failed. Your submission was still recorded.</p>
                </div>
              )}

              {/* Rating update error (if rating update failed) */}
              {selectedTask.rating_update_status === 'failed' && selectedTask.rating_update_error && (
                <div className="task-detail-section rating-error">
                  <h4>Rating Update Error</h4>
                  <p>{selectedTask.rating_update_error}</p>
                  <p className="error-hint">Your worker rating may not have been updated correctly.</p>
                </div>
              )}
            </div>

            <div className="task-modal-actions">
              {selectedTask.status === 'claimed' && (
                <button
                  className="action-btn start-btn"
                  onClick={() => {
                    handleStartTask(selectedTask.id);
                    setSelectedTask(null);
                  }}
                >
                  Start Working
                </button>
              )}
              <button className="action-btn close-btn" onClick={() => setSelectedTask(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTasks;
